import {
  Flex,
  Heading,
  Table,
  Button,
  TextInput,
  Checkbox,
  Select,
  createForm,
  FieldCompatibleProps,
  Card,
  Text,
  Spinner,
} from '@applyboard/crystal-ui';
import { StyledTable } from '../../../../../../components/uie-fix/StyledTable';
import { CheckMarkOutlineIcon, CloseOutlineIcon } from '@applyboard/ui-icons';
import { Course } from '@ab/ams-school-api/types/private/v1';
import { useState, useMemo, useCallback, forwardRef, useEffect } from 'react';
import {
  useUpsertApplicationGpaConfiguration,
  GpaCalculatorParams,
} from '../../../../data/useUpsertApplicationGpaConfiguration';
import { ApplicationGpa } from '@ab/ams-school-api/src/resources/applications/applicationGpa/types';
import { GpaCalculationParameters } from '@ab/ams-school-api/src/resources/applicationGpaCalculationParameters/types';
import styled from '@emotion/styled';

const { Form, Field } = createForm<{ courses: string }>();

interface GradeInputProps {
  readonly rowId: string;
  readonly grades: Readonly<Record<string, string>>;
  readonly setGrades: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  readonly getAriaLabel: (rowId: string, fieldType: string) => string;
}

function GradeInput({ rowId, grades, setGrades, getAriaLabel }: GradeInputProps) {
  const [localValue, setLocalValue] = useState('');
  const [hasLocalValue, setHasLocalValue] = useState(false);

  const displayValue = hasLocalValue ? localValue : grades[rowId] ?? '';

  return (
    <TextInput
      aria-label={getAriaLabel(rowId, 'grade')}
      intent="secondary"
      onChange={(value: string) => {
        setLocalValue(value);
        setHasLocalValue(true);
      }}
      onBlur={() => {
        if (hasLocalValue) {
          setGrades({ ...grades, [rowId]: localValue });
          setHasLocalValue(false);
        }
      }}
      size="md"
      type="text"
      value={displayValue}
    />
  );
}

// Custom field component
interface GpaCalculatorFieldProps extends FieldCompatibleProps<string> {
  readonly courses: ReadonlyArray<Course>;
  readonly onValidationChange?: (isValid: boolean) => void;
}

const GpaCalculatorField = forwardRef<HTMLDivElement, GpaCalculatorFieldProps>(
  ({ courses, value, onChange, invalid, helpText, onValidationChange }, ref) => {
    // Parse the string value to get the course data
    const parseValue = (stringValue: string): GpaCalculatorParams['courses'] => {
      try {
        return stringValue ? JSON.parse(stringValue) : [];
      } catch {
        return [];
      }
    };

    const stringifyValue = (courses: GpaCalculatorParams['courses']): string => {
      return JSON.stringify(courses);
    };

    // Initialize state from the parsed value
    const initialCourseData = parseValue(value);

    // Generate consistent row IDs for initialization
    const initTimestamp = useMemo(() => Date.now(), []);
    const initialRowIds = useMemo(() => {
      if (initialCourseData.length > 0) {
        return initialCourseData.map((_, index) => `row-${initTimestamp}-${index}`);
      }
      return [`row-${initTimestamp}`];
    }, [initialCourseData, initTimestamp]);

    const [tableRows, setTableRows] = useState<string[]>(initialRowIds);

    const [selectedCourses, setSelectedCourses] = useState<{ [rowId: string]: string }>(() => {
      const initial: { [rowId: string]: string } = {};
      let index = 0;
      for (const course of initialCourseData) {
        const rowId = initialRowIds[index];
        initial[rowId] = course.id;
        index++;
      }
      return initial;
    });

    const [grades, setGrades] = useState<{ [rowId: string]: string }>(() => {
      const initial: { [rowId: string]: string } = {};
      let index = 0;
      for (const course of initialCourseData) {
        const rowId = initialRowIds[index];
        initial[rowId] = course.grade.toString();
        index++;
      }
      return initial;
    });

    const [includedCompetitive, setIncludedCompetitive] = useState<{ [rowId: string]: boolean }>(() => {
      const initial: { [rowId: string]: boolean } = {};
      let index = 0;
      for (const course of initialCourseData) {
        const rowId = initialRowIds[index];
        initial[rowId] = course.includedCompetitive;
        index++;
      }
      return initial;
    });

    const [includedOverall, setIncludedOverall] = useState<{ [rowId: string]: boolean }>(() => {
      const initial: { [rowId: string]: boolean } = {};
      let index = 0;
      for (const course of initialCourseData) {
        const rowId = initialRowIds[index];
        initial[rowId] = course.includedOverall;
        index++;
      }
      return initial;
    });

    const getAriaLabel = useCallback(
      (rowId: string, fieldType: string): string => {
        const courseId = selectedCourses[rowId];
        const course = courses.find((c) => c.id === courseId);
        const courseName = course?.name || 'unselected course';

        switch (fieldType) {
          case 'courseSelect':
            return `Select course for row ${tableRows.indexOf(rowId) + 1}`;
          case 'grade':
            return `Grade percentage for row ${tableRows.indexOf(rowId) + 1}: ${courseName}`;
          case 'includedCompetitive':
            return `Include row ${tableRows.indexOf(rowId) + 1}: ${courseName} for competitive grade calculation`;
          case 'includedOverall':
            return `Include row ${tableRows.indexOf(rowId) + 1}: ${courseName} for overall grade calculation`;
          default:
            return `${fieldType} for ${courseName}`;
        }
      },
      [selectedCourses, courses, tableRows]
    );

    // Update the parent form whenever internal state changes
    const updateFormValue = useCallback(() => {
      const aggregatedCourseData: GpaCalculatorParams['courses'] = tableRows
        .filter((rowId) => selectedCourses[rowId]) // Only include rows with selected courses
        .map((rowId) => {
          const courseId = selectedCourses[rowId];
          const parsedGrade = Number(grades[rowId]);
          return {
            id: courseId,
            grade: Number.isNaN(parsedGrade) ? 0 : parsedGrade,
            includedCompetitive: !!includedCompetitive[rowId],
            includedOverall: !!includedOverall[rowId],
          };
        });

      const stringValue = stringifyValue(aggregatedCourseData);
      onChange(stringValue);
    }, [tableRows, selectedCourses, grades, includedCompetitive, includedOverall, onChange]);

    // Validate form completeness
    const validateForm = useCallback(() => {
      // Check if all rows have both course and grade selected
      const isValid = tableRows.every((rowId) => {
        const hasCourse = selectedCourses[rowId] && selectedCourses[rowId].trim() !== '';
        const hasGrade = grades[rowId] && grades[rowId].trim() !== '' && !Number.isNaN(Number(grades[rowId]));
        return hasCourse && hasGrade;
      });

      onValidationChange?.(isValid);
      return isValid;
    }, [tableRows, selectedCourses, grades, onValidationChange]);

    // Call updateFormValue and validation whenever state changes
    useEffect(() => {
      updateFormValue();
      validateForm();
    }, [updateFormValue, validateForm]);

    const createTableRow = useCallback(
      (rowId: string): GpaCalculatorTableProps => {
        return {
          equivalentCourse: (
            <StyledSelectWrapper>
              <Select
                appearance="styled"
                value={selectedCourses[rowId] ?? ''}
                onChange={(value: string) => {
                  setSelectedCourses({ ...selectedCourses, [rowId]: value });
                }}
                placeholder="Select a course"
                aria-label={getAriaLabel(rowId, 'courseSelect')}
              >
                {courses.map((course) => (
                  <Select.Option key={course.id} value={course.id} label={course.name} />
                ))}
              </Select>
            </StyledSelectWrapper>
          ),
          grade: <GradeInput rowId={rowId} grades={grades} setGrades={setGrades} getAriaLabel={getAriaLabel} />,
          includedCompetitive: (
            <Checkbox
              value={includedCompetitive[rowId] ?? false}
              onChange={(checked: boolean) => {
                setIncludedCompetitive({ ...includedCompetitive, [rowId]: checked });
              }}
              aria-label={getAriaLabel(rowId, 'includedCompetitive')}
            />
          ),
          includedOverall: (
            <Checkbox
              value={includedOverall[rowId] ?? false}
              onChange={(checked: boolean) => {
                setIncludedOverall({ ...includedOverall, [rowId]: checked });
              }}
              aria-label={getAriaLabel(rowId, 'includedOverall')}
            />
          ),
          removeAction: (
            <Button
              intent="negative"
              variant="transparent"
              disabled={tableRows.length === 1}
              onClick={() => {
                const filteredRows = tableRows.filter((id) => id !== rowId);

                if (filteredRows.length === 0) {
                  // Last row being removed - add a new empty row to maintain at least one row
                  const newRowId = `row-${Date.now()}`;
                  setTableRows([newRowId]);
                } else {
                  // Still have rows - just remove this one
                  setTableRows(filteredRows);
                }

                // Clean up state for removed row
                const newSelectedCourses = { ...selectedCourses };
                const newGrades = { ...grades };
                const newIncludeCompetitive = { ...includedCompetitive };
                const newIncludeOverall = { ...includedOverall };
                delete newSelectedCourses[rowId];
                delete newGrades[rowId];
                delete newIncludeCompetitive[rowId];
                delete newIncludeOverall[rowId];
                setSelectedCourses(newSelectedCourses);
                setGrades(newGrades);
                setIncludedCompetitive(newIncludeCompetitive);
                setIncludedOverall(newIncludeOverall);
              }}
            >
              Remove
            </Button>
          ),
        };
      },
      [selectedCourses, courses, grades, includedCompetitive, includedOverall, tableRows, getAriaLabel]
    );

    const formattedRows = useMemo(() => {
      return tableRows.map((rowId) => createTableRow(rowId));
    }, [tableRows, createTableRow]);

    const addNewRow = () => {
      const newRowId = `row-${Date.now()}`;
      setTableRows([...tableRows, newRowId]);
    };

    return (
      <div ref={ref}>
        <GpaCalculatorTable courseRows={formattedRows} />
        <Flex justify="start" pt={4}>
          <Button intent="secondary" emphasis="highlighted" onClick={addNewRow}>
            Add course
          </Button>
        </Flex>
        {invalid && helpText && <div style={{ color: 'red', marginTop: '8px', fontSize: '14px' }}>{helpText}</div>}
      </div>
    );
  }
);

GpaCalculatorField.displayName = 'GpaCalculatorField';

// View state helper for cleaner rendering
type ViewState = 'loading' | 'readOnly' | 'editing';

function getViewState(params: {
  hasCalculatedGpa: boolean;
  isEditing: boolean;
  isLoadingCalculateGpa: boolean;
  isSubmitting: boolean;
}): ViewState {
  const loading = params.isLoadingCalculateGpa || params.isSubmitting;
  if (loading) return 'loading';
  if (params.hasCalculatedGpa && !params.isEditing) return 'readOnly';
  return 'editing';
}

export function GpaCalculator({
  courses,
  applicationId,
  gpa,
  gpaCalculationParameters,
}: Readonly<{
  courses: ReadonlyArray<Course>;
  applicationId: string;
  gpa?: ApplicationGpa;
  gpaCalculationParameters?: GpaCalculationParameters;
}>) {
  const { calculateGpa, isLoadingCalculateGpa } = useUpsertApplicationGpaConfiguration(applicationId);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  // Check if GPA has been calculated (both gpa and gpaCalculationParameters exist)
  const hasCalculatedGpa = Boolean(gpa && gpaCalculationParameters);

  // Reset edit mode when GPA calculation completes
  useEffect(() => {
    if (isSubmitting && !isLoadingCalculateGpa) {
      // Clear submitting once the mutation + refetch complete
      setIsSubmitting(false);
      // Exit edit mode only if GPA data is available (i.e., read-only view can be shown)
      if (hasCalculatedGpa && isEditing) {
        setIsEditing(false);
      }
    }
  }, [isSubmitting, isLoadingCalculateGpa, hasCalculatedGpa, isEditing]);

  const viewState = getViewState({
    hasCalculatedGpa,
    isEditing,
    isLoadingCalculateGpa,
    isSubmitting,
  });

  // Prepare initial values for edit mode
  const initialCoursesArr: GpaCalculatorParams['courses'] =
    hasCalculatedGpa && gpaCalculationParameters ? gpaCalculationParameters.attributes.courses : [];

  return (
    <>
      <Heading level={4}>Courses and Grades</Heading>

      {viewState === 'loading' && (
        <>
          <LoadingTable />
          <Flex justify="end" pt={4}>
            <Button intent="secondary" emphasis="outlined" disabled>
              Edit
            </Button>
          </Flex>
          <LoadingGpaCards />
        </>
      )}

      {viewState === 'readOnly' && gpa && gpaCalculationParameters && (
        <>
          <GpaReadOnlyTable courses={courses} gpaCalculationParameters={gpaCalculationParameters} />
          <Flex justify="end" pt={4}>
            <Button
              intent="secondary"
              emphasis="outlined"
              onClick={() => {
                setIsEditing(true);
                // Reset validation state when entering edit mode
                setIsFormValid(false);
              }}
            >
              Edit
            </Button>
          </Flex>
          <ReadOnlyGpaCards gpa={gpa} />
        </>
      )}

      {viewState === 'editing' && (
        <EditGpaForm
          initialCourses={initialCoursesArr}
          courses={courses}
          onSubmit={async (coursesPayload) => {
            setIsSubmitting(true);
            try {
              await calculateGpa({ courses: coursesPayload });
            } catch (error) {
              setIsSubmitting(false);
              throw error;
            }
          }}
          isFormValid={isFormValid}
          setIsFormValid={setIsFormValid}
          onCancel={isEditing ? () => setIsEditing(false) : undefined}
        />
      )}
    </>
  );
}

type GpaCalculatorTableProps = {
  equivalentCourse: React.ReactNode;
  grade: React.ReactNode;
  includedCompetitive: React.ReactNode;
  includedOverall: React.ReactNode;
  removeAction: React.ReactNode;
};

function GpaCalculatorTable({ courseRows }: Readonly<{ courseRows: ReadonlyArray<GpaCalculatorTableProps> }>) {
  return (
    <StyledTable
      columnWidths={{
        grade: 100,
        includedCompetitive: 132,
        includedOverall: 132,
        removeAction: 100,
      }}
      columns={[
        {
          header: 'Equivalent course',
          accessorKey: 'equivalentCourse',
          cell: (row: GpaCalculatorTableProps) => <Table.Cell>{row.equivalentCourse}</Table.Cell>,
        },
        {
          header: 'Grade % (0-100)',
          accessorKey: 'grade',
          cell: (row: GpaCalculatorTableProps) => <Table.Cell>{row.grade}</Table.Cell>,
        },
        {
          header: 'Include for Overall Grade',
          accessorKey: 'includedOverall',
          cell: (row: GpaCalculatorTableProps) => (
            <Flex justify="center">
              <Table.Cell>{row.includedOverall}</Table.Cell>
            </Flex>
          ),
        },
        {
          header: 'Include for Competitive Grade',
          accessorKey: 'includedCompetitive',
          cell: (row: GpaCalculatorTableProps) => (
            <Flex justify="center">
              <Table.Cell>{row.includedCompetitive}</Table.Cell>
            </Flex>
          ),
        },
        {
          header: 'Actions',
          accessorKey: 'removeAction',
          cell: (row: GpaCalculatorTableProps) => (
            <Flex justify="center">
              <Table.Cell>{row.removeAction}</Table.Cell>
            </Flex>
          ),
        },
      ]}
      data={[...courseRows]}
    ></StyledTable>
  );
}

// Loading components
const LoadingTable = () => (
  <Flex justify="center" align="center" py={8}>
    <Spinner size="lg" />
  </Flex>
);

// Read-only GPA cards
function ReadOnlyGpaCards({ gpa }: Readonly<{ gpa: ApplicationGpa }>) {
  return (
    <Flex gap={4}>
      <Flex.Item width="fill">
        <Card aria-label="Overall Grade">
          <Flex direction="column" gap={0} px={2} align="center">
            <Text variant="labelL" contrast="high">
              Overall Grade %
            </Text>
            <Text variant="titleL" intent="positive">
              {gpa.overallGrade ? `${gpa.overallGrade.toFixed(2)}%` : 'N/A'}
            </Text>
          </Flex>
        </Card>
      </Flex.Item>
      <Flex.Item width="fill">
        <Card aria-label="Competitive Grade">
          <Flex direction="column" gap={0} px={2} align="center">
            <Text variant="labelL" contrast="high">
              Competitive Grade %
            </Text>
            <Text variant="titleL" intent="positive">
              {gpa.competitiveGrade ? `${gpa.competitiveGrade.toFixed(2)}%` : 'N/A'}
            </Text>
          </Flex>
        </Card>
      </Flex.Item>
    </Flex>
  );
}

type GpaReadOnlyTableProps = {
  equivalentCourse: string;
  grade: string;
  includedCompetitive: boolean;
  includedOverall: boolean;
};

function GpaReadOnlyTable({
  courses,
  gpaCalculationParameters,
}: Readonly<{
  courses: ReadonlyArray<Course>;
  gpaCalculationParameters: GpaCalculationParameters;
}>) {
  const readOnlyRows: GpaReadOnlyTableProps[] = useMemo(() => {
    return gpaCalculationParameters.attributes.courses.map((configCourse) => {
      const course = courses.find((c) => c.id === configCourse.id);
      return {
        equivalentCourse: course?.name || 'Unknown Course',
        grade: configCourse.grade.toString(),
        includedCompetitive: configCourse.includedCompetitive,
        includedOverall: configCourse.includedOverall,
      };
    });
  }, [courses, gpaCalculationParameters]);

  return (
    <StyledTable
      columnWidths={{
        grade: 100,
        includedCompetitive: 132,
        includedOverall: 132,
      }}
      columns={[
        {
          header: 'Equivalent course',
          accessorKey: 'equivalentCourse',
          cell: (row: GpaReadOnlyTableProps) => <Table.TextCell>{row.equivalentCourse}</Table.TextCell>,
        },
        {
          header: 'Grade % (0-100)',
          accessorKey: 'grade',
          cell: (row: GpaReadOnlyTableProps) => (
            <Table.Cell>
              <Flex justify="center">{row.grade}</Flex>
            </Table.Cell>
          ),
        },
        {
          header: 'Include for Overall Grade',
          accessorKey: 'includedOverall',
          cell: (row: GpaReadOnlyTableProps) => (
            <Table.Cell>
              <Flex justify="center">
                {row.includedOverall ? (
                  <CheckMarkOutlineIcon size="md" intent="positive" />
                ) : (
                  <CloseOutlineIcon size="md" intent="negative" />
                )}
              </Flex>
            </Table.Cell>
          ),
        },
        {
          header: 'Include for Competitive Grade',
          accessorKey: 'includedCompetitive',
          cell: (row: GpaReadOnlyTableProps) => (
            <Table.Cell>
              <Flex justify="center">
                {row.includedCompetitive ? (
                  <CheckMarkOutlineIcon size="md" intent="positive" />
                ) : (
                  <CloseOutlineIcon size="md" intent="negative" />
                )}
              </Flex>
            </Table.Cell>
          ),
        },
      ]}
      data={readOnlyRows}
    ></StyledTable>
  );
}

const LoadingGpaCards = () => (
  <Flex gap={4}>
    <Flex.Item width="fill">
      <Card aria-label="Loading Overall Grade">
        <Flex direction="column" gap={0} px={2} align="center" py={4}>
          <Spinner size="md" />
        </Flex>
      </Card>
    </Flex.Item>
    <Flex.Item width="fill">
      <Card aria-label="Loading Competitive Grade">
        <Flex direction="column" gap={0} px={2} align="center" py={4}>
          <Spinner size="md" />
        </Flex>
      </Card>
    </Flex.Item>
  </Flex>
);

// Simple form wrapper for editing courses
function EditGpaForm({
  initialCourses,
  courses,
  onSubmit,
  isFormValid,
  setIsFormValid,
  onCancel,
}: Readonly<{
  initialCourses: GpaCalculatorParams['courses'];
  courses: ReadonlyArray<Course>;
  onSubmit: (payload: GpaCalculatorParams['courses']) => Promise<void>;
  isFormValid: boolean;
  setIsFormValid: (v: boolean) => void;
  onCancel?: () => void;
}>) {
  return (
    <Form
      key={JSON.stringify(initialCourses)}
      defaultValues={{ courses: JSON.stringify(initialCourses) }}
      onSubmit={async (values) => {
        const parsedCourses = JSON.parse(values.courses);
        await onSubmit(parsedCourses);
      }}
    >
      <Field
        name="courses"
        as={GpaCalculatorField}
        courses={courses}
        onValidationChange={setIsFormValid}
        validate={(value) => {
          try {
            const parsedCourses = JSON.parse(value);
            if (!Array.isArray(parsedCourses)) {
              return 'Invalid course data format';
            }
            return true;
          } catch {
            return 'Invalid course data format';
          }
        }}
      />
      <Flex justify="end" pt={4} gap={2}>
        {onCancel && (
          <Button intent="secondary" emphasis="outlined" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" intent="secondary" emphasis="highlighted" disabled={!isFormValid}>
          Save all
        </Button>
      </Flex>
    </Form>
  );
}

// Styled wrapper for Select to fix min-width issue
const StyledSelectWrapper = styled.div`
  /* Target the div that contains the trigger label */
  div[role='combobox'] > div {
    min-width: 0;
  }
`;
